// Ruteplanlegging: klikk waypoints, se rute + nummererte punkt + total lengde.
// Enter/dblklikk fullfører, Esc avbryter. (Veiruting/terreng-least-cost er en
// senere forfining; dette gir direkte-rute med korrekt geodesisk lengde.)
import maplibregl, { type Map as MlMap, GeoJSONSource, type LngLat } from 'maplibre-gl';
import distance from '@turf/distance';
import { point } from '@turf/helpers';
import { featureCollection, type GeoFeature } from '@skade/contracts';
import type { Kartmodus } from '../map/kartmodus.js';
import { hevVerktoylag, naarKartetErKlart } from '../map/verktoylag.js';

export const ROUTE_ID = 'route';
const ROUTE_LAG = ['route-line', 'route-wp', 'route-wp-num'] as const;

export class RouteTool {
  private active = false;
  private wps: LngLat[] = [];
  private popup?: maplibregl.Popup;

  constructor(private map: MlMap, private modus?: Kartmodus) {
    naarKartetErKlart(map, () => this.initLag());
    // ⚠ DET VAR DETTE VERKTØYET SOM STO BAK «et løsrevet tall 177.19 km ved siden
    // av, uten forklaring» under en avstandsmåling: ruta ble aldri fullført, og
    // ingen ting slo den av. Popup og klikklytter levde videre inn i neste
    // verktøy. Nå meldes verktøyet inn i `Kartmodus`, som stopper det.
    this.modus?.registrer({ id: ROUTE_ID, navn: 'PLANLEGGER RUTE', stopp: () => this.detach() });
  }

  private initLag() {
    if (this.map.getSource('route')) return;
    this.map.addSource('route', { type: 'geojson', data: featureCollection([]) });
    this.map.addLayer({
      id: 'route-line', type: 'line', source: 'route',
      paint: { 'line-color': '#5b9bd5', 'line-width': 3, 'line-opacity': 0.85 },
    });
    this.map.addLayer({
      id: 'route-wp', type: 'circle', source: 'route',
      filter: ['==', '$type', 'Point'],
      paint: { 'circle-radius': 6, 'circle-color': '#5b9bd5', 'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 2 },
    });
    this.map.addLayer({
      id: 'route-wp-num', type: 'symbol', source: 'route',
      filter: ['==', '$type', 'Point'],
      layout: { 'text-field': ['get', 'n'], 'text-font': ['Open Sans Regular'], 'text-size': 10 },
      paint: { 'text-color': '#0a0c0a' },
    });
  }

  toggle() { this.active ? this.stop() : this.start(); }

  start() {
    if (this.active) return; // uten denne ble klikklytteren registrert på nytt
    this.modus?.taOver(ROUTE_ID);
    this.active = true;
    this.wps = [];
    this.initLag();
    hevVerktoylag(this.map, ROUTE_LAG);
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('click', this.onClick);
    this.map.on('dblclick', this.onDblClick);
    this.map.on('contextmenu', this.onContext);
    this.map.doubleClickZoom.disable();
    this.popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: 'measure-popup' });
    window.addEventListener('keydown', this.onKey);
  }

  /** Kobler fra lyttere (Esc/Enter/dblklikk) uten å tømme den tegnede ruta. */
  private detach() {
    if (!this.active) return;
    this.active = false;
    this.map.getCanvas().style.cursor = '';
    this.map.off('click', this.onClick);
    this.map.off('dblclick', this.onDblClick);
    this.map.off('contextmenu', this.onContext);
    this.map.doubleClickZoom.enable();
    this.popup?.remove();
    this.popup = undefined;
    window.removeEventListener('keydown', this.onKey);
    this.modus?.frigi(ROUTE_ID);
  }

  stop() {
    this.detach();
    this.wps = [];
    (this.map.getSource('route') as GeoJSONSource)?.setData(featureCollection([]) as never);
    this.onEndring?.();
  }

  /** Meldes når rutas punkter endrer seg, så objektlista kan tegnes på nytt. */
  onEndring?: () => void;

  get erAktiv(): boolean { return this.active; }
  get antallWp(): number { return this.wps.length; }

  /**
   * Første veipunkt — objektlistas `flyTo`-mål. `null` når ruta er tom.
   *
   * Funn 7 i UX-rapporten: etter at ruta var fullført sto `active = false`, så
   * `toggle()` startet en NY rute i stedet for å fjerne den — ruta lå igjen i
   * kartet uten synlig eier. Nå er den et objekt i lista med sin egen fjern-vei.
   */
  get forstePunkt(): { lon: number; lat: number } | null {
    const p = this.wps[0];
    return p ? { lon: p.lng, lat: p.lat } : null;
  }

  /** Total rutelengde i meter (0 for under to punkt). */
  get lengdeM(): number {
    let sum = 0;
    for (let i = 1; i < this.wps.length; i += 1) {
      // Samme geodesiske avstand verktøyet selv viser i etiketten — ett tall,
      // én kilde. To ulike lengder for samme rute er nøyaktig den klassen feil
      // som ga «et løsrevet 177.19 km» i QA-runden.
      sum += distance(
        point([this.wps[i - 1].lng, this.wps[i - 1].lat]),
        point([this.wps[i].lng, this.wps[i].lat]),
        { units: 'meters' },
      );
    }
    return sum;
  }

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); this.stop(); }
    else if (e.key === 'Enter') { e.preventDefault(); this.onFinish(); }
  };
  private onClick = (e: maplibregl.MapMouseEvent) => {
    this.wps.push(e.lngLat);
    this.render();
  };
  private onDblClick = (e: maplibregl.MapMouseEvent) => { e.preventDefault(); this.onFinish(); };
  private onContext = (e: maplibregl.MapMouseEvent) => { e.preventDefault(); this.stop(); };
  // Fullfør: behold ruta på kartet, men koble fra lyttere (verktøyet «henger»
  // ikke — nye klikk legger ikke lenger til waypoints).
  private onFinish = () => { this.detach(); this.onEndring?.(); };

  private render() {
    const kilde = this.map.getSource('route') as GeoJSONSource | undefined;
    if (!kilde) return;
    const coords = this.wps.map((p) => [p.lng, p.lat] as [number, number]);
    const feats: GeoFeature[] = [
      { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} },
      ...this.wps.map<GeoFeature>((p, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        properties: { n: String(i + 1) },
      })),
    ];
    kilde.setData(featureCollection(feats) as never);

    const total = this.lengdeM;
    const last = this.wps[this.wps.length - 1];
    if (last && this.wps.length > 1) {
      const km = total / 1000;
      // Grov gangtid: 5 km/t.
      const min = Math.round((km / 5) * 60);
      this.popup
        ?.setLngLat(last)
        .setHTML(`<b>Rute ${km.toFixed(2)} km</b><br><span class="muted">~${min} min gange · ${this.wps.length} WP</span>`)
        .addTo(this.map);
    }
  }
}
